// Per-block visual style overrides. Stacks on top of the block's own
// visual fields (HeroBlock.background, etc.) — this editor controls the
// outer wrapper's appearance: background, border, radius, shadow, text
// color, alignment.
//
// Padding and margin are intentionally NOT here — they belong with width
// and height in the shared "Size & Position" section (see
// SizeAndPositionEditor.tsx) so editors find layout controls in one place.

import { useState } from 'react'
import { ChevronDown, ChevronRight } from 'lucide-react'
import type {
  Align,
  Background,
  Block,
  BlockBorder,
  BlockStyle,
  FontFamilyPreset,
  ShadowPreset,
} from './types'
import ColorValueRow from './ColorValueRow'
import { AssetPickerField } from './AssetPicker'
import BackgroundExtrasEditor, {
  BACKGROUND_KIND_OPTIONS,
  backgroundForKind,
  isKnownBackgroundKind,
  type BackgroundExtrasSupport,
} from './BackgroundExtrasEditor'
import { blockStyleLayer, setBlockStyleLayer, FONT_STACKS } from './theme'
import { blockHasItems, blockItemNoun } from '@twasta/page-schema'
import { useViewport } from './ViewportContext'
import BreakpointHint from './BreakpointHint'

interface Props {
  block: Block
  project: string
  onChange: (b: Block) => void
  // Start expanded — used when this editor lives inside a "Common" tab
  // where the user has already chosen to look at shared properties, so
  // there's no value in hiding padding/margin/etc behind another toggle.
  defaultOpen?: boolean
}

const FIELD_LABEL: React.CSSProperties = {
  fontSize: '0.8125rem',
  fontWeight: 600,
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
  color: 'var(--color-text-secondary)',
  marginBottom: 4,
  display: 'block',
}

const INPUT: React.CSSProperties = {
  width: '100%',
  padding: '6px 10px',
  fontSize: '0.9125rem',
  background: 'var(--color-bg)',
  border: '1px solid var(--color-border)',
  borderRadius: 6,
  color: 'var(--color-text)',
  outline: 'none',
}

const ROW: React.CSSProperties = {
  display: 'flex', gap: 6, alignItems: 'center',
}

export default function BlockStyleEditor({ block, project, onChange, defaultOpen }: Props) {
  // The active device breakpoint (driven by the toolbar DeviceToggle)
  // selects which style layer we edit: desktop → block.style, tablet /
  // mobile → block.responsive[viewport]. Switching device in the toolbar
  // both previews and edits that breakpoint.
  const viewport = useViewport()
  const [open, setOpen] = useState(defaultOpen || !!block.style || !!block.responsive)
  const style: BlockStyle = blockStyleLayer(block, viewport) || {}
  // Only blocks that render REPEATED elements get a hover-scope choice; on a
  // single-unit block (image, heading) there is nothing to choose between.
  const hasItems = blockHasItems(block.type)
  const itemNoun = blockItemNoun(block.type)

  const patch = (next: Partial<BlockStyle>) => {
    const merged: BlockStyle = { ...style, ...next }
    // Drop unset keys so the persisted JSON stays minimal, then keep the
    // style object while ANY key remains. A hand-maintained allow-list of
    // "meaningful" keys used to sit here and silently dropped every field it
    // didn't name (transition, hoverScope) once the last named one cleared.
    for (const k of Object.keys(merged) as (keyof BlockStyle)[]) {
      if (merged[k] === undefined) delete merged[k]
    }
    onChange(setBlockStyleLayer(block, viewport, Object.keys(merged).length ? merged : undefined))
  }

  /** Patch the hover state, pruning it away once it is empty. */
  const patchHover = (next: Partial<NonNullable<BlockStyle['hover']>>) => {
    const merged = { ...(style.hover || {}), ...next }
    for (const k of Object.keys(merged) as (keyof typeof merged)[]) {
      if (merged[k] === undefined || merged[k] === '') delete merged[k]
    }
    patch({ hover: Object.keys(merged).length ? merged : undefined })
  }

  /** Patch per-block typography, pruning it away once it is empty. */
  const patchType = (next: Partial<NonNullable<BlockStyle['typography']>>) => {
    const merged: Record<string, unknown> = { ...(style.typography || {}), ...next }
    for (const k of Object.keys(merged)) {
      if (merged[k] === undefined || merged[k] === '') delete merged[k]
    }
    patch({
      typography: Object.keys(merged).length
        ? (merged as NonNullable<BlockStyle['typography']>)
        : undefined,
    })
  }

  const reset = () => onChange(setBlockStyleLayer(block, viewport, undefined))

  return (
    <div style={{
      borderTop: '1px solid var(--color-border)',
      paddingTop: 10, marginTop: 4,
      display: 'flex', flexDirection: 'column', gap: 8,
    }}>
      <button
        onClick={() => setOpen((v) => !v)}
        style={{
          display: 'flex', alignItems: 'center', gap: 6,
          background: 'transparent', border: 'none', padding: 0,
          cursor: 'pointer', color: 'var(--color-text)',
          fontSize: '0.8425rem', fontWeight: 600,
        }}
      >
        {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        <span style={{ ...FIELD_LABEL, marginBottom: 0 }}>Style</span>
        {Object.keys(style).length > 0 && (
          <span style={{ marginLeft: 'auto', fontSize: '0.8125rem', color: 'var(--color-accent)' }}>
            customized
          </span>
        )}
      </button>

      {open && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <BreakpointHint viewport={viewport} />
          {/* Background */}
          <div>
            <label style={miniLabel}>Background</label>
            <BackgroundEditor
              background={style.background}
              project={project}
              onChange={(bg) => patch({ background: bg })}
            />
          </div>

          {/* Text color + alignment */}
          <div style={ROW}>
            <div style={{ flex: 1 }}>
              <label style={miniLabel}>Text color</label>
              <ColorRow
                value={style.textColor || ''}
                placeholder="inherit"
                onChange={(c) => patch({ textColor: c || undefined })}
              />
            </div>
            <div style={{ flex: 1 }}>
              <label style={miniLabel}>Align</label>
              <select
                value={style.align || ''}
                onChange={(e) => patch({ align: (e.target.value || undefined) as Align | undefined })}
                style={INPUT}
              >
                <option value="">Inherit</option>
                <option value="left">Left</option>
                <option value="center">Center</option>
                <option value="right">Right</option>
              </select>
            </div>
          </div>

          {/* Padding & margin live in SizeAndPositionEditor — they're
              co-located with width / height there. */}

          {/* Border */}
          <BorderEditor border={style.border} onChange={(b) => patch({ border: b })} />

          {/* Radius + shadow */}
          <div style={ROW}>
            <div style={{ flex: 1 }}>
              <label style={miniLabel}>Radius (px)</label>
              <input
                type="number" min={0} max={80}
                value={style.borderRadius ?? ''}
                onChange={(e) => {
                  const v = e.target.value === '' ? undefined : parseInt(e.target.value, 10)
                  patch({ borderRadius: typeof v === 'number' && !isNaN(v) ? v : undefined })
                }}
                placeholder="inherit"
                style={INPUT}
              />
            </div>
            <div style={{ flex: 1 }}>
              <label style={miniLabel}>Shadow</label>
              <select
                value={style.shadow || ''}
                onChange={(e) => patch({ shadow: (e.target.value || undefined) as ShadowPreset | undefined })}
                style={INPUT}
              >
                <option value="">Default</option>
                <option value="none">None (removes an inherited shadow)</option>
                <option value="sm">Small</option>
                <option value="md">Medium</option>
                <option value="lg">Large</option>
                <option value="xl">Extra-large</option>
                {unrecognisedOption(style.shadow, SHADOW_VALUES)}
              </select>
            </div>
          </div>

          {/* ── Hover / focus state ──
              Emitted as real CSS (`:hover` and `:focus-within`), because an
              inline style can't express a state. This is what turns a static
              card into one that responds — the most visible gap versus any
              modern site. Focus rides along so it's reachable by keyboard. */}
          <SubGroup label="Hover state" active={!!style.hover && Object.keys(style.hover).length > 0}>
            {/* Scope. Only meaningful on a block that renders REPEATED
                elements — a card_grid is ONE block containing many cards, so
                a wrapper-scoped hover lifts the whole grid at once. Hidden
                entirely on single-unit blocks, where there is no choice to
                make. */}
            {hasItems && (
              <div>
                <label style={miniLabel}>Applies to</label>
                <select
                  value={style.hoverScope || 'items'}
                  onChange={(e) => patch({ hoverScope: e.target.value as 'block' | 'items' | 'both' })}
                  style={INPUT}
                >
                  <option value="items">Each {itemNoun}</option>
                  <option value="block">The whole block</option>
                  <option value="both">Both</option>
                  {unrecognisedOption(style.hoverScope, ['items', 'block', 'both'])}
                </select>
                <p style={hint}>
                  “Each {itemNoun}” responds to the one under the cursor —
                  usually what you want on a grid. “Both” moves two things at
                  once and often reads as a glitch.
                </p>
              </div>
            )}
            <div style={ROW}>
              <div style={{ flex: 1 }}>
                <label style={miniLabel}>Lift (px)</label>
                <input
                  type="number" min={-24} max={24}
                  value={style.hover?.translateY ?? ''}
                  onChange={(e) => patchHover({ translateY: numOrUndef(e.target.value) })}
                  placeholder="e.g. -4"
                  style={INPUT}
                />
              </div>
              <div style={{ flex: 1 }}>
                <label style={miniLabel}>Scale</label>
                <input
                  type="number" min={0.8} max={1.3} step={0.01}
                  value={style.hover?.scale ?? ''}
                  onChange={(e) => patchHover({ scale: numOrUndef(e.target.value) })}
                  placeholder="e.g. 1.03"
                  style={INPUT}
                />
              </div>
            </div>
            <div style={ROW}>
              <div style={{ flex: 1 }}>
                <label style={miniLabel}>Shadow</label>
                <select
                  value={style.hover?.shadow || ''}
                  onChange={(e) => patchHover({ shadow: (e.target.value || undefined) as ShadowPreset | undefined })}
                  style={INPUT}
                >
                  <option value="">No change</option>
                  <option value="none">None</option>
                  <option value="sm">Small</option>
                  <option value="md">Medium</option>
                  <option value="lg">Large</option>
                  <option value="xl">Extra-large</option>
                  {unrecognisedOption(style.hover?.shadow, SHADOW_VALUES)}
                </select>
              </div>
              <div style={{ flex: 1 }}>
                <label style={miniLabel}>Transition (ms)</label>
                <input
                  type="number" min={0} max={2000} step={50}
                  value={style.transition ?? ''}
                  onChange={(e) => patch({ transition: numOrUndef(e.target.value) })}
                  placeholder="200"
                  style={INPUT}
                />
              </div>
            </div>
            <div>
              <label style={miniLabel}>Text color on hover</label>
              <ColorRow
                value={style.hover?.textColor || ''}
                placeholder="no change"
                onChange={(c) => patchHover({ textColor: c || undefined })}
              />
            </div>
            <div>
              <label style={miniLabel}>Background on hover</label>
              <BackgroundEditor
                background={style.hover?.background}
                project={project}
                onChange={(bg) => patchHover({ background: bg })}
              />
            </div>
            <BorderEditor
              label="Border on hover"
              border={style.hover?.border}
              onChange={(b) => patchHover({ border: b })}
            />
            <div style={ROW}>
              <div style={{ flex: 1 }}>
                <label style={miniLabel}>Radius on hover (px)</label>
                <input
                  type="number" min={0} max={80}
                  value={style.hover?.borderRadius ?? ''}
                  onChange={(e) => patchHover({ borderRadius: numOrUndef(e.target.value) })}
                  placeholder="no change"
                  style={INPUT}
                />
              </div>
              <div style={{ flex: 1 }}>
                <label style={miniLabel}>Opacity on hover</label>
                <input
                  type="number" min={0} max={1} step={0.05}
                  value={style.hover?.opacity ?? ''}
                  onChange={(e) => patchHover({ opacity: numOrUndef(e.target.value) })}
                  placeholder="no change"
                  style={INPUT}
                />
              </div>
            </div>
            <div>
              <label style={miniLabel}>Backdrop blur on hover (px)</label>
              <input
                type="number" min={0} max={40}
                value={style.hover?.backdropBlur ?? ''}
                onChange={(e) => patchHover({ backdropBlur: numOrUndef(e.target.value) })}
                placeholder="no change"
                style={INPUT}
              />
            </div>
            <p style={hint}>
              Movement (lift / scale) is skipped for visitors who ask for
              reduced motion; the color and shadow change still applies.
            </p>
          </SubGroup>

          {/* ── Typography ──
              `textColor` used to be the only typographic override, so an
              author couldn't make one heading display-sized without a custom
              class. */}
          <SubGroup label="Typography" active={!!style.typography && Object.keys(style.typography).length > 0}>
            <div style={ROW}>
              <div style={{ flex: 1 }}>
                <label style={miniLabel}>Size (px)</label>
                <input
                  type="number" min={8} max={200}
                  value={style.typography?.fontSize ?? ''}
                  onChange={(e) => patchType({ fontSize: numOrUndef(e.target.value) })}
                  placeholder="theme"
                  style={INPUT}
                />
              </div>
              <div style={{ flex: 1 }}>
                <label style={miniLabel}>Weight</label>
                <select
                  value={style.typography?.fontWeight ?? ''}
                  onChange={(e) => patchType({ fontWeight: numOrUndef(e.target.value) })}
                  style={INPUT}
                >
                  <option value="">Theme</option>
                  {[300, 400, 500, 600, 700, 800, 900].map((w) => (
                    <option key={w} value={w}>{w}</option>
                  ))}
                </select>
              </div>
            </div>
            <div style={ROW}>
              <div style={{ flex: 1 }}>
                <label style={miniLabel}>Tracking (em)</label>
                <input
                  type="number" step={0.01} min={-0.1} max={0.5}
                  value={style.typography?.letterSpacing ?? ''}
                  onChange={(e) => patchType({ letterSpacing: numOrUndef(e.target.value) })}
                  placeholder="0"
                  style={INPUT}
                />
              </div>
              <div style={{ flex: 1 }}>
                <label style={miniLabel}>Line height</label>
                <input
                  type="number" step={0.05} min={0.8} max={3}
                  value={style.typography?.lineHeight ?? ''}
                  onChange={(e) => patchType({ lineHeight: numOrUndef(e.target.value) })}
                  placeholder="theme"
                  style={INPUT}
                />
              </div>
            </div>
            <div style={ROW}>
              <div style={{ flex: 1 }}>
                <label style={miniLabel}>Transform</label>
                <select
                  value={style.typography?.textTransform || ''}
                  onChange={(e) => patchType({ textTransform: (e.target.value || undefined) as NonNullable<BlockStyle['typography']>['textTransform'] })}
                  style={INPUT}
                >
                  <option value="">Theme</option>
                  <option value="none">None</option>
                  <option value="uppercase">UPPERCASE</option>
                  <option value="lowercase">lowercase</option>
                  <option value="capitalize">Capitalize</option>
                  {unrecognisedOption(style.typography?.textTransform, ['none', 'uppercase', 'lowercase', 'capitalize'])}
                </select>
              </div>
              <div style={{ flex: 1 }}>
                <label style={miniLabel}>Max width (px)</label>
                <input
                  type="number" min={120} max={1600}
                  value={style.typography?.maxWidth ?? ''}
                  onChange={(e) => patchType({ maxWidth: numOrUndef(e.target.value) })}
                  placeholder="measure"
                  style={INPUT}
                />
              </div>
            </div>
            <div>
              <label style={miniLabel}>Font family</label>
              <select
                value={style.typography?.fontFamily || ''}
                onChange={(e) => patchType({ fontFamily: (e.target.value || undefined) as FontFamilyPreset | undefined })}
                style={{
                  ...INPUT,
                  fontFamily: style.typography?.fontFamily ? FONT_STACKS[style.typography.fontFamily] : undefined,
                }}
              >
                <option value="">Theme</option>
                {FONT_FAMILIES.map((f) => (
                  <option key={f} value={f} style={{ fontFamily: FONT_STACKS[f] }}>{f}</option>
                ))}
                {unrecognisedOption(style.typography?.fontFamily, FONT_FAMILIES)}
              </select>
            </div>
            <div>
              <label style={miniLabel}>Gradient text</label>
              <div style={ROW}>
                <ColorRow
                  value={style.typography?.textGradient?.from || ''}
                  placeholder="from"
                  onChange={(c) => patchType({
                    textGradient: c
                      ? { from: c, to: style.typography?.textGradient?.to || c, angle: style.typography?.textGradient?.angle }
                      : undefined,
                  })}
                />
                <ColorRow
                  value={style.typography?.textGradient?.to || ''}
                  placeholder="to"
                  onChange={(c) => patchType({
                    textGradient: style.typography?.textGradient?.from
                      ? { from: style.typography.textGradient.from, to: c || style.typography.textGradient.from, angle: style.typography.textGradient.angle }
                      : undefined,
                  })}
                />
              </div>
              {style.typography?.textGradient && (
                <div style={{ ...ROW, marginTop: 6 }}>
                  <label style={{ ...miniLabel, marginBottom: 0, whiteSpace: 'nowrap' }}>Angle (deg)</label>
                  <input
                    type="number" min={0} max={360}
                    value={style.typography.textGradient.angle ?? ''}
                    onChange={(e) => {
                      const g = style.typography!.textGradient!
                      const angle = numOrUndef(e.target.value)
                      const { angle: _drop, ...rest } = g
                      void _drop
                      patchType({ textGradient: angle === undefined ? rest : { ...rest, angle } })
                    }}
                    placeholder="90"
                    style={INPUT}
                  />
                </div>
              )}
            </div>
          </SubGroup>

          {/* ── Layering ──
              Negative margins (in Size & Position) plus a z-index are how a
              card overlaps a section boundary — the "designed, not
              generated" tell. */}
          <SubGroup
            label="Layering & effects"
            active={typeof style.zIndex === 'number' || typeof style.backdropBlur === 'number' || typeof style.opacity === 'number'}
          >
            <div style={ROW}>
              <div style={{ flex: 1 }}>
                <label style={miniLabel}>Z-index</label>
                <input
                  type="number" min={-10} max={999}
                  value={style.zIndex ?? ''}
                  onChange={(e) => patch({ zIndex: numOrUndef(e.target.value) })}
                  placeholder="auto"
                  style={INPUT}
                />
              </div>
              <div style={{ flex: 1 }}>
                <label style={miniLabel}>Opacity</label>
                <input
                  type="number" min={0} max={1} step={0.05}
                  value={style.opacity ?? ''}
                  onChange={(e) => patch({ opacity: numOrUndef(e.target.value) })}
                  placeholder="1"
                  style={INPUT}
                />
              </div>
            </div>
            <div>
              <label style={miniLabel}>Backdrop blur (px)</label>
              <input
                type="number" min={0} max={40}
                value={style.backdropBlur ?? ''}
                onChange={(e) => patch({ backdropBlur: numOrUndef(e.target.value) })}
                placeholder="glassmorphism over imagery"
                style={INPUT}
              />
            </div>
          </SubGroup>

          <div>
            <label style={miniLabel}>Custom CSS class</label>
            <input
              type="text"
              value={style.className || ''}
              onChange={(e) => patch({ className: e.target.value || undefined })}
              placeholder="my-custom-block (optional)"
              style={{ ...INPUT, fontFamily: 'monospace' }}
            />
          </div>

          {block.style && (
            <button
              onClick={reset}
              className="btn btn-ghost btn-sm"
              style={{ alignSelf: 'flex-start', color: 'var(--color-error)' }}
            >
              Reset all overrides
            </button>
          )}
        </div>
      )}
    </div>
  )
}

const miniLabel: React.CSSProperties = {
  fontSize: '0.8125rem',
  color: 'var(--color-text-secondary)',
  marginBottom: 4,
  display: 'block',
}

const hint: React.CSSProperties = {
  margin: 0, fontSize: '0.8125rem', lineHeight: 1.45,
  color: 'var(--color-text-secondary)',
}

/** Parse a numeric input, treating an empty field as "unset". */
function numOrUndef(v: string): number | undefined {
  if (v === '') return undefined
  const n = Number(v)
  return Number.isFinite(n) ? n : undefined
}

const SHADOW_VALUES: readonly string[] = ['none', 'sm', 'md', 'lg', 'xl']
const FONT_FAMILIES = Object.keys(FONT_STACKS) as FontFamilyPreset[]

/**
 * A saved value the select doesn't list would otherwise display as the first
 * option (looking like "Default") — list it explicitly instead.
 */
function unrecognisedOption(value: string | undefined, known: readonly string[]) {
  if (!value || known.includes(value)) return null
  return <option value={value}>{value} (unrecognised)</option>
}

/**
 * A collapsible group inside the Style panel. The panel would otherwise be a
 * wall of ~25 controls; grouping keeps the common ones (background, border,
 * radius) in view and tucks the specialist ones one click away, with a dot
 * when the group has been customised so nothing is hidden silently.
 */
function SubGroup({ label, active, children }: {
  label: string; active?: boolean; children: React.ReactNode
}) {
  const [open, setOpen] = useState(!!active)
  return (
    <div style={{ borderTop: '1px solid var(--color-border)', paddingTop: 8 }}>
      <button
        onClick={() => setOpen((v) => !v)}
        style={{
          display: 'flex', alignItems: 'center', gap: 6, width: '100%',
          background: 'transparent', border: 'none', padding: 0, cursor: 'pointer',
          color: 'var(--color-text)', fontSize: '0.8125rem', fontWeight: 600,
        }}
      >
        {open ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
        <span>{label}</span>
        {active && (
          <span style={{ marginLeft: 'auto', width: 6, height: 6, borderRadius: '50%', background: 'var(--color-accent)' }} />
        )}
      </button>
      {open && <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 8 }}>{children}</div>}
    </div>
  )
}

// ─── Border editor ───────────────────────────────────────

export function BorderEditor({
  border,
  onChange,
  label = 'Border',
}: {
  border: BlockBorder | undefined
  onChange: (next: BlockBorder | undefined) => void
  label?: string
}) {
  const enabled = !!border
  const b: BlockBorder = border || { width: 1, color: '#e2e8f0', style: 'solid' }
  return (
    <div>
      <div style={{ ...ROW, justifyContent: 'space-between', marginBottom: 4 }}>
        <label style={{ ...miniLabel, marginBottom: 0 }}>{label}</label>
        <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: '0.8125rem', color: 'var(--color-text-secondary)' }}>
          <input
            type="checkbox"
            checked={enabled}
            onChange={(e) => onChange(e.target.checked ? b : undefined)}
          />
          Enabled
        </label>
      </div>
      {enabled && (
        <div style={ROW}>
          <input
            type="number" min={0} max={20}
            value={b.width}
            onChange={(e) => onChange({ ...b, width: parseInt(e.target.value, 10) || 0 })}
            placeholder="1"
            style={{ ...INPUT, width: 60 }}
          />
          <select
            value={b.style || 'solid'}
            onChange={(e) => onChange({ ...b, style: e.target.value as BlockBorder['style'] })}
            style={{ ...INPUT, width: 90 }}
          >
            <option value="solid">solid</option>
            <option value="dashed">dashed</option>
            <option value="dotted">dotted</option>
          </select>
          <ColorRow
            value={b.color}
            onChange={(c) => onChange({ ...b, color: c })}
          />
        </div>
      )}
    </div>
  )
}

// ─── Color row ───────────────────────────────────────────
// Thin wrapper around the shared ColorValueRow so existing call sites
// keep their tight (value, onChange, placeholder) shape.

export function ColorRow(props: {
  value: string
  onChange: (v: string) => void
  placeholder?: string
}) {
  return <ColorValueRow {...props} />
}

// ─── Background sub-editor ───────────────────────────────
// Kind + the basic fields; everything else the schema carries (video,
// multi-stop / radial gradients, image overlay + overlay gradient) comes from
// the shared BackgroundExtrasEditor so the two background editors can't drift.

/**
 * What the Style-panel path paints: block and section `style.background` go
 * through `theme.ts backgroundCss`, which paints a video's poster only, a
 * still gradient for the animated kind, and no overlay gradient.
 */
const STYLE_SURFACE_SUPPORT: BackgroundExtrasSupport = {
  videoLayer: false,
  drift: false,
  overlayGradient: false,
}

export function BackgroundEditor({
  background,
  project,
  onChange,
  support = STYLE_SURFACE_SUPPORT,
}: {
  background: Background | undefined
  project: string
  onChange: (bg: Background | undefined) => void
  support?: BackgroundExtrasSupport
}) {
  // No override → show "Inherit (unset)", NOT "None". `undefined` is the
  // genuine no-override (inherit) state, so the dropdown must reflect the
  // user's "Inherit" choice on reopen instead of falling back to "None"
  // (which looked like the selection wasn't saved).
  const kind: Background['kind'] | 'unset' = background ? background.kind : 'unset'
  const isGradient = kind === 'gradient' || kind === 'gradient_animated'
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <select
        value={kind}
        onChange={(e) => {
          if (e.target.value === 'unset') return onChange(undefined)
          // Rebuilt from the fields that mean something for the new kind —
          // Image ↔ Video keeps the overlay, Gradient ↔ Animated keeps stops.
          onChange(backgroundForKind(background, e.target.value as Background['kind']))
        }}
        style={INPUT}
      >
        <option value="unset">Inherit (unset)</option>
        {BACKGROUND_KIND_OPTIONS.map((o) => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
        {background && !isKnownBackgroundKind(background.kind) && (
          <option value={background.kind}>{String(background.kind)} (unrecognised)</option>
        )}
      </select>

      {kind === 'color' && background && (
        <ColorRow
          value={background.color || '#ffffff'}
          onChange={(c) => onChange({ ...background, kind: 'color', color: c })}
        />
      )}
      {isGradient && background && (
        <>
          <ColorRow
            value={background.gradientFrom || '#0f172a'}
            onChange={(c) => onChange({ ...background, gradientFrom: c })}
          />
          <ColorRow
            value={background.gradientTo || '#1e3a8a'}
            onChange={(c) => onChange({ ...background, gradientTo: c })}
          />
          <input
            type="number" min={0} max={360}
            value={background.gradientAngle ?? ''}
            onChange={(e) => onChange({
              ...background,
              gradientAngle: numOrUndef(e.target.value),
            })}
            placeholder="angle (deg) — 135"
            style={INPUT}
          />
        </>
      )}
      {kind === 'image' && background && (
        <>
          <AssetPickerField
            project={project}
            value={background.imageUrl}
            accept="image/*"
            onChange={(url) => onChange({ ...background, kind: 'image', imageUrl: url })}
          />
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={!!background.parallax}
              onChange={(e) => onChange({ ...background, parallax: e.target.checked })}
            />
            Parallax on scroll
          </label>
          {background.parallax && (
            <span style={{ fontSize: 13, color: 'var(--color-text-secondary)' }}>
              Image stays fixed while the page scrolls over it (desktop; static on mobile).
            </span>
          )}
        </>
      )}
      {background && (
        <BackgroundExtrasEditor
          value={background}
          project={project}
          onChange={onChange}
          support={support}
        />
      )}
    </div>
  )
}
